import React from 'react';
import {
  Box,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
  FormControlLabel,
} from '@mui/material';

const MODE_OPTIONS = [
  { value: 'exact_text', label: 'Exact text' },
  { value: 'text_with_synonyms', label: 'Text with accepted variants' },
  { value: 'numeric', label: 'Numeric' },
  { value: 'numeric_with_rounding', label: 'Numeric with rounding' },
  { value: 'numeric_with_unit', label: 'Numeric with unit' },
  { value: 'equivalent_units', label: 'Equivalent units' },
  { value: 'fraction_decimal_percent_equivalence', label: 'Fraction / decimal / percent equivalence' },
  { value: 'expression_equivalence', label: 'Expression equivalence' },
  { value: 'ordered_list', label: 'Ordered list' },
  { value: 'unordered_list', label: 'Unordered list' },
];

const ROUNDING_OPTIONS = [
  { value: 'decimal_places', label: 'Decimal places' },
  { value: 'significant_figures', label: 'Significant figures' },
];

const ROUNDING_METHODS = [
  { value: 'half_up', label: 'Half up' },
  { value: 'half_down', label: 'Half down' },
  { value: 'half_even', label: 'Half even' },
];

const buildConfig = (config = {}) => ({
  mode: config.mode || 'exact_text',
  acceptedAnswers: Array.isArray(config.acceptedAnswers) ? config.acceptedAnswers : [],
  tolerance: config.tolerance ?? 0,
  unitRequired: Boolean(config.unitRequired),
  allowEquivalentUnits: Boolean(config.allowEquivalentUnits),
  acceptedUnitAliases: config.acceptedUnitAliases || {},
  rounding: {
    type: config.rounding?.type || 'decimal_places',
    places: config.rounding?.places ?? 2,
    figures: config.rounding?.figures ?? 3,
    method: config.rounding?.method || 'half_up',
  },
});

export default function AnswerEvaluationFields({
  questionType,
  value,
  onChange,
}) {
  if (questionType !== 'SHORT_ANSWER') {
    return null;
  }

  const config = buildConfig(value);

  const emit = (next) => {
    onChange?.({
      ...config,
      ...next,
      rounding: { ...config.rounding, ...(next.rounding || {}) },
    });
  };

  const acceptedAnswersText = config.acceptedAnswers.join(', ');
  const unitAliasesText = Array.isArray(config.acceptedUnitAliases?.default)
    ? config.acceptedUnitAliases.default.join(', ')
    : '';

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Choose how free-text answers should be matched. Existing exact-match behavior stays the default.
      </Typography>
      <Stack spacing={1.5}>
        <TextField
          select
          label="Answer evaluation mode"
          value={config.mode}
          onChange={(e) => emit({ mode: e.target.value })}
          fullWidth
          size="small"
        >
          {MODE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        {config.mode === 'text_with_synonyms' && (
          <TextField
            label="Accepted variants"
            value={acceptedAnswersText}
            onChange={(e) => emit({
              acceptedAnswers: e.target.value.split(',').map((item) => item.trim()).filter(Boolean),
            })}
            fullWidth
            size="small"
            helperText="Comma-separated variants such as cm, centimeters, centimetres"
          />
        )}

        {['numeric', 'fraction_decimal_percent_equivalence', 'expression_equivalence'].includes(config.mode) && (
          <TextField
            label="Tolerance"
            type="number"
            value={config.tolerance}
            onChange={(e) => emit({ tolerance: e.target.value === '' ? 0 : Number(e.target.value) })}
            fullWidth
            size="small"
            helperText="Use 0 for exact comparison"
          />
        )}

        {config.mode === 'numeric_with_rounding' && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              select
              label="Rounding type"
              value={config.rounding.type}
              onChange={(e) => emit({ rounding: { type: e.target.value } })}
              fullWidth
              size="small"
            >
              {ROUNDING_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={config.rounding.type === 'significant_figures' ? 'Figures' : 'Places'}
              type="number"
              value={config.rounding.type === 'significant_figures' ? config.rounding.figures : config.rounding.places}
              onChange={(e) => emit({
                rounding: config.rounding.type === 'significant_figures'
                  ? { figures: Number(e.target.value || 0) }
                  : { places: Number(e.target.value || 0) },
              })}
              fullWidth
              size="small"
            />
            <TextField
              select
              label="Rounding method"
              value={config.rounding.method}
              onChange={(e) => emit({ rounding: { method: e.target.value } })}
              fullWidth
              size="small"
            >
              {ROUNDING_METHODS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        )}

        {['numeric_with_unit', 'equivalent_units'].includes(config.mode) && (
          <>
            <TextField
              label="Accepted unit aliases"
              value={unitAliasesText}
              onChange={(e) => emit({
                acceptedUnitAliases: {
                  ...config.acceptedUnitAliases,
                  default: e.target.value.split(',').map((item) => item.trim()).filter(Boolean),
                },
              })}
              fullWidth
              size="small"
              helperText="Optional extra aliases for the unit in the correct answer"
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.unitRequired}
                    onChange={(e) => emit({ unitRequired: e.target.checked })}
                  />
                }
                label="Unit required"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={config.mode === 'equivalent_units' || config.allowEquivalentUnits}
                    onChange={(e) => emit({
                      mode: e.target.checked ? 'equivalent_units' : 'numeric_with_unit',
                      allowEquivalentUnits: e.target.checked,
                    })}
                  />
                }
                label="Allow equivalent units"
              />
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
}
